import { AlertTriangle } from 'lucide-react-native';
import { VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

interface ReviewFlagBannerProps {
  requiresReview: boolean;
  reason?: string | null;
  flaggedAt?: string | null;
}

// Sentinel written by the automatic path in VoiceCallsController.recordConsentFromCall
// when the agent could not capture the lead's own wording.
const AUTOMATIC_CONSENT_REFUSAL = 'consent_refused_during_call';

/**
 * Surfaces the human-review queue flag on the lead detail.
 *
 * A consent refusal during a call blocks further calls on its own; this banner
 * is the other half — it tells the commercial team a decision is waiting for
 * them (contact by another channel, discard, or correct the data). Renders
 * nothing for leads that need no review (FLOSEAI-560).
 */
export function ReviewFlagBanner({
  requiresReview,
  reason,
  flaggedAt,
}: Readonly<ReviewFlagBannerProps>) {
  const { t } = useTranslation();

  if (!requiresReview) return null;

  const reasonText =
    reason === AUTOMATIC_CONSENT_REFUSAL
      ? t('leads.detail.review.reasonConsentRefused')
      : reason?.trim() || null;

  const flaggedOn = flaggedAt
    ? t('leads.detail.review.flaggedOn', { date: flaggedAt.slice(0, 10) })
    : null;

  return (
    <VStack
      spacing="xxs"
      testID="lead_detail_review_flag"
      accessibilityRole="alert"
      style={{
        borderLeftWidth: 3,
        borderLeftColor: colors.status.warning.light,
        paddingLeft: tokens.spacing.sm,
      }}
    >
      <Text variant="label" uppercase color={colors.status.warning.light}>
        <AlertTriangle size={12} color={colors.status.warning.light} strokeWidth={1.8} />
        {t('leads.detail.review.title')}
      </Text>
      {reasonText ? (
        <Text variant="caption" color={colors.text.secondary}>
          {reasonText}
        </Text>
      ) : null}
      {flaggedOn ? (
        <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 10 }}>
          {flaggedOn}
        </Text>
      ) : null}
    </VStack>
  );
}
